// @ts-nocheck
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, X, MessageSquare, Loader2, ChevronDown, ChevronUp } from 'lucide-react';
import OrbitVoice from './OrbitVoice';
import OrbitMarkdown from './OrbitMarkdown';
import { streamOnboarding, type OnboardingEvent, type StreamOnboardingOptions } from '../../services/onboardingApi';
import { BRAND } from '../../constants/brand';
import { phaseLabelFor } from '../../utils/phaseLabels';

/**
 * OrbitDock — persistent floating chat widget (Google-Docs-style).
 *
 * Collapsed: a small favicon pill pinned bottom-center. Expands on click
 * into a composer card with voice + text input. Streams to the same
 * /onboarding/stream endpoint so the conversation continues the existing
 * session (thanks to client.onboarding_session hydration).
 *
 * Three new responsibilities (2026-04-22) that make Orbit feel "alive"
 * across surfaces instead of siloed in a chat bubble:
 *
 *   1. **Phase visibility** — watches tool_event(task:create/update) and
 *      surfaces the currently-running task in the header ("Connecting
 *      GitHub…", "Looking up your business…"). The user SEES Orbit
 *      working, not a static "thinking…" dot.
 *
 *   2. **Cross-surface dispatch** — when the agent calls connectApp and
 *      a redirectUrl arrives, OrbitDock emits `orbit:request-connect`.
 *      The active page's connection panel (ConnectCanvas / Dashboard tile)
 *      catches the event and lights up the matching card so the user
 *      clicks in the page, not in chat. Same for `orbit:client-established`
 *      so every surface's clientId stays in sync after lookupClient/createClient.
 *
 *   3. **Lane + surface awareness** — forwards `lane`/`surface` to the
 *      backend stream so the prompt knows whether this is a fresh onboarding
 *      (Website/Upload/Connect lane) or an ongoing dashboard conversation.
 *      Stops the "please share your website URL" demand on the Connect lane.
 */

export interface OrbitDockProps {
  sessionId?: string | null;
  clientId?: string | null;
  onAgentEvent?: (event: OnboardingEvent) => void;
  anchor?: 'bottom-center' | 'bottom-right';
  autoOpen?: boolean;
  greeting?: string;
  /** Which Orbit lane is this mount on? Shapes the prompt so the agent
   *  doesn't demand a URL on Connect / demand docs on Upload / restart
   *  onboarding on Dashboard. */
  lane?: StreamOnboardingOptions['lane'];
  /** Which surface? 'onboarding' (guest flow), 'dashboard' (My Business), or 'knowledge' (KB browser). */
  surface?: StreamOnboardingOptions['surface'];
}

type DockState = 'dock' | 'composer' | 'streaming';

interface TranscriptLine {
  who: 'user' | 'orbit';
  text: string;
  id: string;
}

interface PhaseInfo {
  id: string;
  label: string;
  detail?: string;
  status: 'running' | 'done' | 'error';
  /** Time the phase entered the running state — used to fade finished phases out. */
  at: number;
}

interface ArchivedReply { id: string; text: string; at: number; }


const OrbitDock: React.FC<OrbitDockProps> = ({
  sessionId: initialSessionId,
  clientId,
  onAgentEvent,
  anchor = 'bottom-center',
  autoOpen = false,
  greeting,
  lane,
  surface,
}) => {
  const [state, setState] = useState<DockState>(autoOpen ? 'composer' : 'dock');
  const [input, setInput] = useState('');
  const [transcript, setTranscript] = useState<TranscriptLine[]>(() => (
    greeting
      ? [{ who: 'orbit', text: greeting, id: 'orbit-greeting' }]
      : []
  ));
  const [streamingText, setStreamingText] = useState('');
  /** Map keyed by task id — lets task:update patch an existing entry. */
  const [phases, setPhases] = useState<Record<string, PhaseInfo>>({});
  /** The current Orbit reply rendered in the dock drawer. Single source of
   *  truth — replaces the per-page OrbitReply panel that used to mirror this. */
  const [currentReply, setCurrentReply] = useState<{ id: string; text: string; done: boolean } | null>(null);
  /** Earlier replies, surfaced via the "Earlier (N)" toggle in the drawer. */
  const [replyHistory, setReplyHistory] = useState<ArchivedReply[]>([]);
  /** When true, the drawer shows past replies under the current one. */
  const [historyOpen, setHistoryOpen] = useState(false);
  const sessionRef = useRef<string | null>(initialSessionId ?? null);
  const clientIdRef = useRef<string | null>(clientId ?? null);
  const abortRef = useRef<null | (() => void)>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const transcriptRef = useRef<HTMLDivElement>(null);
  // "Just closed" suppression — avoids bouncing the composer back open.
  const suppressOpenUntilRef = useRef<number>(0);
  // Prevent duplicate orbit:request-connect dispatches for the same redirectUrl.
  const dispatchedConnectsRef = useRef<Set<string>>(new Set());

  // Keep clientId ref in sync with prop updates.
  useEffect(() => {
    if (clientId) clientIdRef.current = clientId;
  }, [clientId]);

  const closeToDock = useCallback(() => {
    suppressOpenUntilRef.current = Date.now() + 600;
    setState('dock');
  }, []);

  const openComposer = useCallback(() => {
    if (Date.now() < suppressOpenUntilRef.current) return;
    setState('composer');
  }, []);

  useEffect(() => {
    if (state === 'composer') {
      const t = setTimeout(() => textareaRef.current?.focus(), 80);
      return () => clearTimeout(t);
    }
  }, [state]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && state === 'composer') {
        closeToDock();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [state, closeToDock]);

  useEffect(() => () => {
    abortRef.current?.();
  }, []);

  useEffect(() => {
    const handler = (ev: Event) => {
      const detail = (ev as CustomEvent).detail || {};
      const seed = typeof detail.text === 'string' ? detail.text : '';
      // External open requests always win — reset the close-suppression window.
      suppressOpenUntilRef.current = 0;
      setState('composer');
      if (seed) setInput(seed);
    };
    window.addEventListener('orbit:open-with-prompt', handler as EventListener);
    return () => window.removeEventListener('orbit:open-with-prompt', handler as EventListener);
  }, []);

  // Reply drawer state — the dock now owns reply rendering (Pass 5 collapsed
  // OrbitReply into the dock for one source of truth). External streams
  // (ResearchDashboard, UploadLane) dispatch `orbit:reply` events; the dock
  // both renders them locally AND continues to dispatch its own when its
  // composer drives a stream — so the same drawer surfaces both paths.
  useEffect(() => {
    const onReply = (ev: Event) => {
      const detail = (ev as CustomEvent).detail || {};
      if (!detail.id || typeof detail.text !== 'string') return;
      setCurrentReply({
        id: String(detail.id),
        text: String(detail.text),
        done: !!detail.done,
      });
      // External reply arriving while the dock is collapsed → open it so the
      // user actually sees what Orbit is saying (mirrors the legacy OrbitReply
      // panel's always-visible behavior). Suppression window is overridden.
      suppressOpenUntilRef.current = 0;
      setState(prev => (prev === 'dock' ? 'composer' : prev));
    };
    const onArchive = (ev: Event) => {
      const detail = (ev as CustomEvent).detail || {};
      if (!detail.id || !String(detail.text || '').trim()) return;
      setReplyHistory(prev => {
        if (prev.some(h => h.id === detail.id)) return prev;
        return [...prev, { id: String(detail.id), text: String(detail.text), at: Date.now() }];
      });
    };
    const onReset = () => {
      setCurrentReply(null);
      setHistoryOpen(false);
    };
    window.addEventListener('orbit:reply', onReply as EventListener);
    window.addEventListener('orbit:reply-archive', onArchive as EventListener);
    window.addEventListener('orbit:reply-reset', onReset as EventListener);
    return () => {
      window.removeEventListener('orbit:reply', onReply as EventListener);
      window.removeEventListener('orbit:reply-archive', onArchive as EventListener);
      window.removeEventListener('orbit:reply-reset', onReset as EventListener);
    };
  }, []);

  // Auto-scroll transcript to bottom on new content.
  useEffect(() => {
    const el = transcriptRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [transcript, streamingText]);

  /** The most recent RUNNING phase — what we show as the header chip. */
  const activePhase = useMemo<PhaseInfo | null>(() => {
    const running = Object.values(phases).filter(p => p.status === 'running');
    if (running.length === 0) return null;
    return running.sort((a, b) => b.at - a.at)[0];
  }, [phases]);

  // Mirror the active phase into OrbitReply while a reply is being streamed —
  // gives the page-level panel the same "Connecting GitHub…" chip OrbitDock's
  // header shows. Fires on every phase change (throttled by React's render).
  useEffect(() => {
    if (state !== 'streaming') return;
    if (!activePhase) return;
    try {
      window.dispatchEvent(new CustomEvent('orbit:reply-phase', {
        detail: { phase: activePhase.label },
      }));
    } catch { /* noop */ }
  }, [activePhase, state]);

  const handleEvent = useCallback((event: OnboardingEvent) => {
    if (event.type === 'session_id' && event.sessionId) {
      sessionRef.current = event.sessionId;
      return;
    }

    if (event.type === 'tool_event') {
      const e = event.e;
      const d = event.d || {};

      // Phase lifecycle
      if (e === 'task:create' || e === 'task:update') {
        const id = String(d.id || '');
        if (!id) return;
        const status = d.status === 'done' ? 'done' : d.status === 'error' ? 'error' : 'running';
        setPhases(prev => {
          const existing = prev[id];
          const label = phaseLabelFor({ id, label: d.label, detail: d.detail, meta: d.meta });
          return {
            ...prev,
            [id]: {
              id,
              label,
              detail: d.detail,
              status,
              at: existing && status === 'running' ? existing.at : Date.now(),
            },
          };
        });
      }

      // Client account established — broadcast so every surface syncs clientId.
      if (e === 'client' && d.clientId) {
        clientIdRef.current = d.clientId;
        try {
          window.dispatchEvent(new CustomEvent('orbit:client-established', { detail: { clientId: d.clientId } }));
        } catch { /* noop */ }
      }

      // connectApp returned a redirect URL → dispatch to the active page's
      // connection panel so the matching card lights up. The card click
      // (user gesture) opens the popup with the cached URL.
      const meta = d.meta || {};
      if (meta.provider && meta.redirectUrl && !dispatchedConnectsRef.current.has(meta.redirectUrl)) {
        dispatchedConnectsRef.current.add(meta.redirectUrl);
        try {
          window.dispatchEvent(new CustomEvent('orbit:request-connect', {
            detail: {
              provider: String(meta.provider),
              redirectUrl: String(meta.redirectUrl),
              composioClientId: meta.composioClientId ? String(meta.composioClientId) : undefined,
              shareUrl: meta.shareUrl ? String(meta.shareUrl) : undefined,
            },
          }));
        } catch { /* noop */ }
      }
    }

    // suggestNextSteps surfaces personalized chips on the active connect panel.
    // Forward verbatim — ConnectCanvas listens on its own stream, but a separate
    // stream that triggers suggestNextSteps (e.g. chat-driven probe) should also
    // reach the panel.
    if (event.type === 'tool_event' && event.e === 'next_steps' && event.d?.provider) {
      try {
        window.dispatchEvent(new CustomEvent('orbit:next-steps', {
          detail: { provider: String(event.d.provider), actions: event.d.actions || [] },
        }));
      } catch { /* noop */ }
    }

    // Intel-field updates from chat → flash the matching tile + trigger a
    // re-fetch on the My Business page. Single event handles both:
    // useTileFlash listens for `orbit:tile-update {section}` and runs the
    // brand-yellow flash on the matching tile; MyBusiness's window listener
    // bumps refreshKey so the new value lands in props.
    if (event.type === 'tool_event' && event.e === 'intel_update' && event.d?.section) {
      try {
        window.dispatchEvent(new CustomEvent('orbit:tile-update', {
          detail: {
            section: String(event.d.section),
            patch: event.d.patch,
            filled_by: event.d.filled_by,
          },
        }));
      } catch { /* noop */ }
    }

    onAgentEvent?.(event);
  }, [onAgentEvent]);

  const handleSend = useCallback((overrideText?: string) => {
    const text = (overrideText ?? input).trim();
    if (!text || state === 'streaming') return;

    setInput('');
    setState('streaming');
    setStreamingText('');
    setPhases({});
    dispatchedConnectsRef.current = new Set();
    setTranscript(prev => [...prev, { who: 'user', text, id: `u-${Date.now()}` }]);

    // The page's OrbitReply panel listens for these — clear the current reply
    // so the user's next turn starts from a blank slate. Previous replies are
    // archived separately (see orbit:reply-archive below).
    const messageId = `o-${Date.now()}`;
    try { window.dispatchEvent(new CustomEvent('orbit:reply-reset')); } catch { /* noop */ }

    // Pass the prior transcript as history so the model has continuity across
    // turns. Previously this was [] which made every turn look like a fresh
    // conversation to the agent — context like "we run jani-king" was lost
    // by the next message. Map our transcript shape to OpenAI-style roles.
    const historyForBackend = transcript.map(line => ({
      role: line.who === 'user' ? 'user' : 'assistant',
      content: line.text,
    }));

    let accumulated = '';
    abortRef.current = streamOnboarding(
      text,
      historyForBackend,
      sessionRef.current,
      undefined,
      (event: OnboardingEvent) => {
        if (event.type === 'message' && typeof event.content === 'string') {
          accumulated += event.content;
          setStreamingText(accumulated);
          // Stream every token to the page-level reply surface.
          try {
            window.dispatchEvent(new CustomEvent('orbit:reply', {
              detail: { id: messageId, text: accumulated, done: false, phase: null },
            }));
          } catch { /* noop */ }
        }
        handleEvent(event);
      },
      (err) => {
        setTranscript(prev => [...prev, { who: 'orbit', text: `⚠️ ${err}`, id: `e-${Date.now()}` }]);
        setStreamingText('');
        setState('composer');
        try {
          window.dispatchEvent(new CustomEvent('orbit:reply', {
            detail: { id: messageId, text: `⚠️ ${err}`, done: true, phase: null },
          }));
        } catch { /* noop */ }
      },
      () => {
        if (accumulated.trim()) {
          setTranscript(prev => [...prev, { who: 'orbit', text: accumulated, id: messageId }]);
        }
        setStreamingText('');
        setState('composer');
        // Finalize + archive — lets the OrbitReply panel move it to history on the
        // next turn, keeping the "current" slot reserved for the freshest reply.
        try {
          window.dispatchEvent(new CustomEvent('orbit:reply', {
            detail: { id: messageId, text: accumulated, done: true, phase: null },
          }));
          if (accumulated.trim()) {
            window.dispatchEvent(new CustomEvent('orbit:reply-archive', {
              detail: { id: messageId, text: accumulated },
            }));
          }
        } catch { /* noop */ }
      },
      { lane, surface },
    );
  }, [input, state, lane, surface, handleEvent]);

  const handleVoiceTranscript = useCallback((speech: string) => {
    if (speech.trim()) handleSend(speech);
  }, [handleSend]);

  // Horizontal centering without `transform` (framer-motion layoutId clobbers transforms).
  const anchorStyle: React.CSSProperties = anchor === 'bottom-right'
    ? { right: 24, bottom: 24 }
    : { left: 0, right: 0, bottom: 24, marginLeft: 'auto', marginRight: 'auto' };

  // Collapsed dock
  if (state === 'dock') {
    if (typeof document === 'undefined') return null;
    return createPortal(
      <motion.button
        layoutId="orbit-dock"
        onClick={openComposer}
        style={{ position: 'fixed', zIndex: 50, ...anchorStyle, width: 48, height: 48, background: 'rgba(255,255,255,0.92)' }}
        whileTap={{ scale: 0.95 }}
        className="flex items-center justify-center rounded-full backdrop-blur-lg shadow-[0_8px_32px_rgba(24,25,29,0.18)] border border-black/5 transition-shadow duration-200 hover:shadow-[0_12px_40px_rgba(24,25,29,0.28)]"
        aria-label="Ask Orbit"
        title="Ask Orbit"
      >
        <div className="w-8 h-8 rounded-full flex items-center justify-center bg-black">
          <img src="/icon-white.png" alt="" className="w-4 h-4 object-contain" />
        </div>
      </motion.button>,
      document.body,
    );
  }

  // Expanded composer
  if (typeof document === 'undefined') return null;
  return createPortal(
    <AnimatePresence>
      <motion.div
        ref={containerRef}
        layoutId="orbit-dock"
        key="composer"
        style={{ position: 'fixed', zIndex: 50, ...anchorStyle, width: 620, maxWidth: 'calc(100vw - 32px)' }}
        className="rounded-2xl shadow-[0_16px_48px_rgba(0,0,0,0.14)] border border-black/[0.04] overflow-hidden"
      >
        <div style={{ background: 'rgba(255,255,255,0.98)', backdropFilter: 'blur(14px)' }}>
          {/* Header — shows current tool phase while streaming so the user sees
              WHAT Orbit is doing, not just "thinking…". */}
          <div className="flex items-center justify-between px-4 pt-3 pb-2 border-b border-gray-100/80">
            <div className="flex items-center gap-2.5 min-w-0 flex-1">
              <div className="w-6 h-6 rounded-full bg-black flex items-center justify-center shadow-sm shrink-0">
                <img src="/icon-white.png" alt="" className="w-3 h-3 object-contain" />
              </div>
              <span className="text-[12px] font-semibold text-gray-800 tracking-tight shrink-0">Orbit</span>

              {state === 'streaming' && (
                <div
                  className="flex items-center gap-1.5 text-[11px] font-medium rounded-full px-2 py-0.5 min-w-0"
                  style={{
                    background: 'linear-gradient(90deg, rgba(24,25,29,0.10) 0%, rgba(24,25,29,0.04) 100%)',
                    color: '#0b0b0f',
                    border: '1px solid rgba(24,25,29,0.18)',
                  }}
                >
                  <Loader2 className="w-3 h-3 animate-spin shrink-0" />
                  <span className="truncate">
                    {activePhase ? activePhase.label : 'Thinking…'}
                  </span>
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={closeToDock}
              onMouseDown={e => e.preventDefault()}
              className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors focus:outline-none shrink-0"
              aria-label="Minimise"
              title="Minimise (Esc)"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>

          {/* Thin context row — just the LAST user turn so they remember what
              they asked. The reply itself renders below in the drawer. */}
          {(() => {
            const lastUser = [...transcript].reverse().find(l => l.who === 'user');
            if (!lastUser) return null;
            return (
              <div className="px-4 pt-2 pb-1 text-[11.5px] text-gray-500 flex items-start gap-2 border-t border-gray-50">
                <span className="text-[10px] font-semibold tracking-[0.1em] uppercase mt-0.5 shrink-0">You</span>
                <span className="truncate">{lastUser.text}</span>
              </div>
            );
          })()}

          {/* Reply drawer — single source of truth for Orbit's voice. Renders
              the current streaming/finished reply in real markdown, with a
              collapsible "Earlier" history of past replies. Replaces the per-
              page OrbitReply panel that used to mirror this state. */}
          {(currentReply || replyHistory.length > 0) && (
            <div className="px-4 pb-3 border-t border-gray-50">
              {currentReply && currentReply.text.trim() && (
                <div
                  className="orbit-dock-prose mt-2 max-h-[55vh] overflow-y-auto pr-1"
                >
                  <OrbitMarkdown content={currentReply.text} />
                  {!currentReply.done && (
                    <span
                      className="inline-block w-[2px] h-[14px] ml-0.5 align-baseline animate-pulse"
                      style={{ background: BRAND.blue }}
                      aria-hidden
                    />
                  )}
                </div>
              )}

              {replyHistory.length > 0 && (
                <div className="mt-3 pt-2 border-t border-gray-100">
                  <button
                    type="button"
                    onClick={() => setHistoryOpen(v => !v)}
                    className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-gray-500 hover:text-gray-800 transition-colors"
                  >
                    <MessageSquare className="w-3 h-3" />
                    {historyOpen ? 'Hide' : 'Show'} earlier ({replyHistory.length})
                    {historyOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                  </button>
                  <AnimatePresence>
                    {historyOpen && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.18 }}
                        className="overflow-hidden mt-2 space-y-3"
                      >
                        {replyHistory.slice().reverse().map(item => (
                          <div
                            key={item.id}
                            className="orbit-dock-prose orbit-dock-prose--archive pl-3 max-h-[180px] overflow-y-auto"
                            style={{ borderLeft: '2px solid rgba(24,25,29,0.18)' }}
                          >
                            <OrbitMarkdown content={item.text} />
                          </div>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )}

              {/* Prose typography — Perplexity / Claude.ai grade. Goals:
                  - Strong heading hierarchy with a colored gradient bar accent
                  - Lead-paragraph rhythm (first ¶ after a heading is full-weight)
                  - Custom bullets + tabular ordered numbers (no default browser glyph)
                  - Soft body color (#1F2937) — not pure black, much easier to read
                  - Refined links with subtle underline that strengthens on hover
                  - Callout-style blockquote for "Key Insight" sections
                  - Tabular numerics for any data-style content
                  Tuned for the ~620px dock width — comfortable measure, no cramping. */}
              <style>{`
                .orbit-dock-prose {
                  font-size: 14.5px;
                  line-height: 1.7;
                  color: #1F2937;
                  letter-spacing: -0.005em;
                  font-feature-settings: 'kern', 'ss01';
                }

                /* ── Headings ─────────────────────────────────────── */
                .orbit-dock-prose h1,
                .orbit-dock-prose h2 {
                  font-size: 16.5px;
                  font-weight: 700;
                  color: #0F172A;
                  margin: 22px 0 6px;
                  letter-spacing: -0.018em;
                  line-height: 1.3;
                  position: relative;
                  padding-left: 12px;
                }
                .orbit-dock-prose h1::before,
                .orbit-dock-prose h2::before {
                  content: '';
                  position: absolute;
                  left: 0;
                  top: 0.3em;
                  bottom: 0.3em;
                  width: 3px;
                  border-radius: 2px;
                  background: linear-gradient(180deg, ${BRAND.blue} 0%, ${BRAND.yellow} 100%);
                }
                .orbit-dock-prose h1:first-child,
                .orbit-dock-prose h2:first-child { margin-top: 4px; }
                .orbit-dock-prose h3 {
                  font-size: 13.5px;
                  font-weight: 600;
                  color: #0F172A;
                  margin: 14px 0 4px;
                  letter-spacing: -0.005em;
                  line-height: 1.4;
                }
                .orbit-dock-prose h4,
                .orbit-dock-prose h5,
                .orbit-dock-prose h6 {
                  font-size: 12.5px;
                  font-weight: 600;
                  color: #374151;
                  margin: 10px 0 3px;
                  text-transform: uppercase;
                  letter-spacing: 0.04em;
                }

                /* ── Paragraphs ───────────────────────────────────── */
                .orbit-dock-prose p { margin: 8px 0; color: #374151; }
                .orbit-dock-prose p:first-child { margin-top: 0; }
                .orbit-dock-prose p:last-child  { margin-bottom: 0; }
                .orbit-dock-prose h1 + p,
                .orbit-dock-prose h2 + p,
                .orbit-dock-prose h3 + p { margin-top: 4px; }
                /* Lead paragraph after a major heading — slightly stronger color */
                .orbit-dock-prose h1 + p,
                .orbit-dock-prose h2 + p { color: #1F2937; }

                /* ── Lists — custom bullets + tabular numbers ─────── */
                .orbit-dock-prose ul,
                .orbit-dock-prose ol {
                  margin: 8px 0;
                  padding-left: 0;
                  list-style: none;
                }
                .orbit-dock-prose li {
                  position: relative;
                  padding-left: 18px;
                  margin: 4px 0;
                  color: #374151;
                  line-height: 1.65;
                }
                .orbit-dock-prose ul > li::before {
                  content: '';
                  position: absolute;
                  left: 4px;
                  top: 0.6em;
                  width: 5px;
                  height: 5px;
                  border-radius: 50%;
                  background: ${BRAND.blue};
                  opacity: 0.55;
                }
                .orbit-dock-prose ol {
                  counter-reset: orbit-list;
                }
                .orbit-dock-prose ol > li {
                  counter-increment: orbit-list;
                  padding-left: 22px;
                }
                .orbit-dock-prose ol > li::before {
                  content: counter(orbit-list) '.';
                  position: absolute;
                  left: 0;
                  top: 0;
                  font-weight: 600;
                  color: ${BRAND.blueDeep};
                  font-variant-numeric: tabular-nums;
                  min-width: 18px;
                }
                /* Nested lists — tighten */
                .orbit-dock-prose li ul,
                .orbit-dock-prose li ol { margin: 3px 0; }

                /* ── Inline emphasis ──────────────────────────────── */
                .orbit-dock-prose strong { color: #0F172A; font-weight: 600; }
                .orbit-dock-prose em     { font-style: italic; color: #374151; }
                .orbit-dock-prose mark   { background: rgba(255,234,0,0.32); color: inherit; padding: 0 2px; border-radius: 2px; }

                /* ── Code ─────────────────────────────────────────── */
                .orbit-dock-prose code {
                  background: rgba(24,25,29,0.08);
                  color: ${BRAND.blueDeep};
                  padding: 1.5px 5px;
                  border-radius: 4px;
                  font-size: 12.5px;
                  font-family: ui-monospace, 'SF Mono', Menlo, Consolas, monospace;
                  font-feature-settings: normal;
                  letter-spacing: 0;
                }
                .orbit-dock-prose pre {
                  background: #F8FAFC;
                  border: 1px solid rgba(17,24,39,0.06);
                  border-radius: 8px;
                  padding: 10px 12px;
                  font-size: 12.5px;
                  margin: 10px 0;
                  overflow-x: auto;
                  line-height: 1.55;
                }
                .orbit-dock-prose pre code {
                  background: transparent;
                  padding: 0;
                  color: inherit;
                  font-size: inherit;
                }

                /* ── Blockquote — callout style for "Key Insight" ── */
                .orbit-dock-prose blockquote {
                  border-left: 3px solid ${BRAND.yellow};
                  background: linear-gradient(90deg, rgba(255,234,0,0.08) 0%, rgba(255,234,0,0.0) 100%);
                  padding: 10px 14px;
                  margin: 12px 0;
                  border-radius: 0 8px 8px 0;
                  color: #374151;
                  font-style: normal;
                }
                .orbit-dock-prose blockquote p { margin: 4px 0; }
                .orbit-dock-prose blockquote strong { color: ${BRAND.blueDeep}; }

                /* ── Links — subtle until hover ───────────────────── */
                .orbit-dock-prose a {
                  color: ${BRAND.blueDeep};
                  text-decoration: underline;
                  text-decoration-color: rgba(24,25,29,0.30);
                  text-decoration-thickness: 1.5px;
                  text-underline-offset: 2.5px;
                  transition: text-decoration-color 0.15s, color 0.15s;
                }
                .orbit-dock-prose a:hover {
                  text-decoration-color: ${BRAND.blueDeep};
                  color: ${BRAND.blue};
                }

                /* ── HR — soft section break ──────────────────────── */
                .orbit-dock-prose hr {
                  border: none;
                  margin: 18px 0;
                  height: 1px;
                  background: linear-gradient(90deg, transparent 0%, rgba(17,24,39,0.10) 50%, transparent 100%);
                }

                /* ── Tables ───────────────────────────────────────── */
                .orbit-dock-prose table {
                  width: 100%;
                  border-collapse: collapse;
                  margin: 12px 0;
                  font-size: 12.5px;
                  font-variant-numeric: tabular-nums;
                }
                .orbit-dock-prose thead { background: rgba(24,25,29,0.04); }
                .orbit-dock-prose th {
                  text-align: left;
                  font-weight: 600;
                  color: #0F172A;
                  padding: 7px 10px;
                  border-bottom: 1px solid rgba(17,24,39,0.10);
                  letter-spacing: -0.005em;
                }
                .orbit-dock-prose td {
                  padding: 7px 10px;
                  border-bottom: 1px solid rgba(17,24,39,0.05);
                  color: #374151;
                }
                .orbit-dock-prose tbody tr:hover { background: rgba(24,25,29,0.025); }
                .orbit-dock-prose tbody tr:last-child td { border-bottom: none; }

                /* ── Selection — brand-tinted ─────────────────────── */
                .orbit-dock-prose ::selection {
                  background: rgba(24,25,29,0.16);
                  color: #0F172A;
                }

                /* ── Archive variant — slightly muted past replies ─── */
                .orbit-dock-prose--archive {
                  font-size: 13px;
                  color: #4B5563;
                }
                .orbit-dock-prose--archive h1,
                .orbit-dock-prose--archive h2 { font-size: 14.5px; margin: 12px 0 4px; }
                .orbit-dock-prose--archive h3 { font-size: 12.5px; }
                .orbit-dock-prose--archive p  { color: #4B5563; }
              `}</style>
            </div>
          )}

          {/* Composer */}
          <div className="px-3 py-3">
            <div className="flex items-end gap-2 bg-gray-50 rounded-xl px-3 py-2 ring-1 ring-inset ring-gray-100 focus-within:ring-gray-200 transition-shadow">
              <textarea
                ref={textareaRef}
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
                placeholder={
                  state === 'streaming'
                    ? (activePhase ? activePhase.label : 'Orbit is thinking…')
                    : 'Tell Orbit about your business — or ask anything…'
                }
                rows={1}
                disabled={state === 'streaming'}
                className="flex-1 bg-transparent text-[13px] resize-none placeholder:text-gray-400 py-1.5 min-h-[28px] max-h-[120px] disabled:opacity-60 border-0 outline-none focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 appearance-none shadow-none"
                style={{ color: BRAND.black, outline: 'none', boxShadow: 'none' }}
              />
              <OrbitVoice
                onTranscript={handleVoiceTranscript}
                size="sm"
                autoSubmit
                promptHint="Business description, question, or request"
                className="shrink-0"
              />
              <button
                onClick={() => handleSend()}
                disabled={!input.trim() || state === 'streaming'}
                className="flex items-center justify-center w-8 h-8 rounded-lg transition-all duration-200 shrink-0 focus:outline-none"
                style={{
                  background: input.trim() && state !== 'streaming' ? BRAND.blue : 'rgba(17,24,39,0.06)',
                  color: input.trim() && state !== 'streaming' ? 'white' : '#9CA3AF',
                  boxShadow: input.trim() && state !== 'streaming' ? '0 4px 12px rgba(24,25,29,0.25)' : 'none',
                }}
                aria-label="Send"
                title="Send (Enter)"
              >
                <Send className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>,
    document.body,
  );
};

export default OrbitDock;
